import { useConfig } from '@/composables';
import axios, { AxiosInstance } from 'axios';
import { container, singleton } from 'tsyringe';
import { FirebaseAppCheck } from './firebase-app-check';

const config = useConfig();
const X_GV_CLIENT = 'ewa-web';

/**
 * How long any `.ewa` request may take before it is aborted (KUDO1-9541).
 *
 * An instance-level default, so it covers the warnings index and detail fetches along with
 * `getOutagesAndClosures`, `getClosures`, `getCentres` and every other call on this instance.
 *
 * Derived from the portal service's Lambda timeout of 10 seconds, declared as
 * `Duration.seconds(10)` in `emergency-wa-portal-service/infra/lib/stack.ts`. Sitting just under it
 * means a function that runs to its own limit is cut off here first, so the client gives up on a
 * request the server has already abandoned rather than waiting out the gateway. Re-derive this if
 * that Lambda timeout moves; the two are not linked by anything but this comment.
 */
export const EWA_REQUEST_TIMEOUT_MS = 8000;

@singleton()
export class AxiosSingleton {
  public ewa: AxiosInstance;
  public s3: AxiosInstance;
  public readonly ewaPublicBff: AxiosInstance;

  constructor() {
    this.ewa = axios.create({
      baseURL: config.value.portalApi,
      // Without this a stalled request never settles, and the repository's in-flight guards clear
      // only via `.finally` — so one stalled socket parks the warnings pipeline across every later
      // poll, not just its own. Applies to every `.ewa` call, not only the warning detail fetches.
      timeout: EWA_REQUEST_TIMEOUT_MS,
    });
    this.s3 = axios.create({
      baseURL: config.value.geospatialBucket,
    });

    this.ewaPublicBff = axios.create({
      baseURL: `${config.value.portalEwaBffApi}/public`,
      headers: {
        'x-gv-client': X_GV_CLIENT,
      },
    });

    this.ewaPublicBff.interceptors.request.use(async (config) => {
      config.headers['x-gv-client'] = X_GV_CLIENT;
      const appCheckToken = await container.resolve<FirebaseAppCheck>(FirebaseAppCheck).getToken();
      if (appCheckToken?.length) {
        config.headers['x-gv-app-check-token'] = appCheckToken;
      }
      return config;
    });
  }
}
